import { useState, useEffect } from 'react';
import { signalerFicheVue } from '../lib/mesure';
import { pixelFicheVue } from '../lib/pixels';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, ImageOff, Truck, Plus, Minus } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { resolveImageUrl } from '../lib/imageUrl';
import { getCategoryLabel } from '../lib/categories';
import { formatPrice } from '../lib/formatPrice';
import {
  FRAIS_LIEGE,
  FRAIS_PERIPHERIE,
  FRAIS_HORS_LIEGE,
  MINIMUM_HORS_LIEGE,
  delaiLivraison,
} from '../lib/delivery';
import CartPlusIcon from '../components/CartPlusIcon';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { t, getLocalizedField, language } = useLanguage();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [quantity, setQuantity] = useState(1);
  // Poids retenu par le visiteur, parmi ceux que la fiche propose.
  const [declinaison, setDeclinaison] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setNotFound(false);
      setImageFailed(false);
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
        // Par défaut, le premier poids encore disponible. Partir sur un
        // format épuisé afficherait « rupture » sur une fiche qui a pourtant
        // des poids en rayon.
        const liste = response.data.declinaisons || [];
        setDeclinaison(liste.find((d) => (d.stock ?? 0) > 0) || liste[0] || null);
        setQuantity(1);
        signalerFicheVue(response.data.id);
        pixelFicheVue({ id: response.data.id, nom: response.data.name_fr, prix: Number(response.data.price) || 0 });
      } catch (error) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    // Le retour en haut de page est traité globalement par ScrollToTop.
  }, [productId]);

  // Ce que le visiteur a sous les yeux : le poids retenu décide du prix, du
  // stock et de la photo. Calculé avant les retours anticipés pour rester
  // lisible depuis les gestionnaires.
  const declinaisons = product?.declinaisons || [];
  const choisie = declinaison || declinaisons[0] || null;
  const prixAffiche = Number(choisie?.price ?? product?.price) || 0;
  const stockDisponible = choisie ? (choisie.stock ?? 0) : (product?.stock ?? 0);

  const handleAddToCart = () => {
    setIsAdding(true);
    addItem(product, quantity, choisie);
    toast.success(t('added'), { description: getLocalizedField(product, 'name') });
    setTimeout(() => setIsAdding(false), 1000);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 lg:pt-24">
        <div className="max-w-6xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-2 gap-12 animate-pulse">
          <div className="aspect-square bg-gray-200" />
          <div className="space-y-4">
            <div className="h-8 bg-gray-200 w-3/4" />
            <div className="h-4 bg-gray-200 w-1/2" />
            <div className="h-12 bg-gray-200 w-1/3" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="min-h-screen pt-20 lg:pt-24 flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-2xl font-bold text-text_primary mb-4">
            {language === 'fr' ? 'Produit introuvable' : 'Product not found'}
          </h1>
          <p className="text-text_secondary mb-6">
            {language === 'fr'
              ? "Ce produit n'existe plus ou n'est pas disponible."
              : 'This product no longer exists or is unavailable.'}
          </p>
          <Link
            to="/products"
            className="inline-block px-6 py-3 bg-primary text-white font-medium hover:bg-primary_hover transition-colors"
          >
            {language === 'fr' ? 'Voir tous les produits' : 'Browse all products'}
          </Link>
        </div>
      </div>
    );
  }

  const name = getLocalizedField(product, 'name');
  const description = getLocalizedField(product, 'description');
  // Une déclinaison peut avoir sa propre photo : deux tiers des groupes
  // fusionnés n'avaient pas la même image d'un format à l'autre.
  const imageSrc = resolveImageUrl(choisie?.image_url || product.image_url);
  const showPlaceholder = !imageSrc || imageFailed;
  const inStock = stockDisponible > 0;
  // URL canonique : toujours l'adresse lisible, même si on est arrivé par l'id
  const productUrl = `https://afrionebd.eu/products/${product.slug || product.id}`;

  // Données structurées : permettent à Google d'afficher le prix et la
  // disponibilité directement dans les résultats de recherche, et donnent aux
  // assistants IA une description exploitable du produit.
  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    sku: product.sku,
    image: imageSrc ? [imageSrc] : undefined,
    category: getCategoryLabel(product.category, 'fr'),
    brand: { '@type': 'Brand', name: product.brand || 'AfriOne BD' },
    offers: {
      '@type': 'Offer',
      url: productUrl,
      price: Number(prixAffiche).toFixed(2),
      priceCurrency: 'EUR',
      availability: inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: 'AfriOne BD' },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: FRAIS_LIEGE.toFixed(2),
          currency: 'EUR',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'BE',
        },
      },
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://afrionebd.eu/' },
      { '@type': 'ListItem', position: 2, name: 'Produits', item: 'https://afrionebd.eu/products' },
      {
        '@type': 'ListItem',
        position: 3,
        name: getCategoryLabel(product.category, 'fr'),
        item: `https://afrionebd.eu/products?category=${product.category}`,
      },
      { '@type': 'ListItem', position: 4, name, item: productUrl },
    ],
  };

  return (
    <div className="min-h-screen pt-20 lg:pt-24 pb-16" data-testid="product-detail-page">
      <SEO
        title={`${name} - AfriOne BD`}
        description={`${description} - Disponible chez AfriOne BD, épicerie africaine livrée à Liège et partout en Belgique.`}
        ogTitle={`${name} - AfriOne BD`}
        ogDescription={description}
        ogImage={imageSrc}
        canonical={productUrl}
        type="product"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Fil d'Ariane */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text_secondary hover:text-text_primary transition-colors mb-6"
          data-testid="product-back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          {language === 'fr' ? 'Retour' : 'Back'}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-start">
          {/* Image — à plat et jamais recadrée : sur une fiche produit, rogner
              la photo cacherait l'étiquette, la marque ou le grammage.
              Fond blanc comme celui des photos du catalogue, pour que le
              cadrage d'une image portrait ne laisse pas de bandes visibles. */}
          <div className="relative bg-white flex items-center justify-center">
            {showPlaceholder ? (
              <div className="w-full aspect-square flex flex-col items-center justify-center gap-3 text-gray-300">
                <ImageOff className="w-16 h-16" />
                <span className="text-sm text-gray-400">
                  {language === 'fr' ? 'Photo à venir' : 'Photo coming soon'}
                </span>
              </div>
            ) : (
              <img
                src={imageSrc}
                alt={name}
                // Taille naturelle, bornée en hauteur : une photo portrait
                // étirée sur toute la largeur de la colonne serait démesurée.
                width={product.image_width || undefined}
                height={product.image_height || undefined}
                className="block h-auto w-auto max-w-full max-h-[560px]"
                onError={() => setImageFailed(true)}
              />
            )}

            {!inStock && (
              <span className="absolute top-0 left-0 px-3 py-1.5 bg-black/70 text-xs font-medium uppercase tracking-wide text-white">
                {language === 'fr' ? 'Rupture' : 'Sold out'}
              </span>
            )}
          </div>

          {/* Informations */}
          <div>
            <Link
              to={`/products?category=${product.category}`}
              className="text-xs uppercase tracking-widest text-primary font-medium hover:underline"
            >
              {getCategoryLabel(product.category, language)}
            </Link>

            <h1
              className="text-2xl lg:text-3xl font-bold font-heading text-text_primary mt-2 tracking-tight"
              data-testid="product-detail-name"
            >
              {name}
            </h1>

            {/* Marque et conditionnement : le client doit savoir exactement ce
                qu'il achète (une unité ou un carton). */}
            {(product.brand || (declinaisons.length <= 1 && product.format)) && (
              <p className="mt-1 text-sm text-text_secondary">
                {product.brand}
                {product.brand && declinaisons.length <= 1 && product.format && ' · '}
                {declinaisons.length <= 1 ? product.format : ''}
              </p>
            )}

            {/* La description est vide pour la plupart des produits : on n'affiche
                le paragraphe que si elle existe. */}
            {description && (
              <p className="mt-4 text-text_secondary leading-relaxed">{description}</p>
            )}

            {/* Choix du poids. Chaque bouton porte son prix : le visiteur
                compare sans avoir à cliquer, ce qui était tout le problème
                quand ces formats étaient des fiches séparées. */}
            {declinaisons.length > 1 && (
              <div className="mt-6" data-testid="declinaisons">
                <p className="text-sm font-medium text-text_primary mb-2">
                  {language === 'fr' ? 'Choisissez votre format' : 'Choose your size'}
                </p>
                <div className="flex flex-wrap gap-2" role="radiogroup"
                     aria-label={language === 'fr' ? 'Format' : 'Size'}>
                  {declinaisons.map((d) => {
                    const retenue = d.id === choisie?.id;
                    const epuise = (d.stock ?? 0) <= 0;
                    return (
                      <button
                        key={d.id}
                        type="button"
                        role="radio"
                        aria-checked={retenue}
                        disabled={epuise}
                        onClick={() => { setDeclinaison(d); setQuantity(1); }}
                        data-testid={`declinaison-${d.id}`}
                        className={`px-4 py-2 border text-left transition ${
                          retenue
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-text_primary/40'
                        } ${epuise ? 'opacity-40 cursor-not-allowed' : ''}`}
                      >
                        <span className="block text-sm font-semibold text-text_primary">{d.format}</span>
                        <span className="block text-xs text-text_secondary mt-0.5">
                          {epuise
                            ? (language === 'fr' ? 'épuisé' : 'sold out')
                            : formatPrice(d.price, language)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-baseline gap-3 mt-6 mb-2">
              <span className="text-3xl lg:text-4xl font-bold text-text_primary" data-testid="product-detail-price">
                {formatPrice(prixAffiche, language)}
              </span>
              <span className="text-sm text-text_secondary">
                {language === 'fr' ? 'TVA comprise' : 'VAT included'}
              </span>
            </div>

            {/* Disponibilité (sans exposer la quantité exacte) */}
            <p className={`text-sm font-semibold mb-6 ${
              !inStock ? 'text-red-600' : stockDisponible <= 10 ? 'text-orange-600' : 'text-green-600'
            }`}>
              {!inStock
                ? (language === 'fr' ? 'Rupture de stock' : 'Out of stock')
                : stockDisponible <= 10
                  ? (language === 'fr' ? 'Derniers exemplaires' : 'Only a few left')
                  : `✓ ${language === 'fr' ? 'En stock' : 'In stock'}`}
            </p>

            {/* Quantité + ajout au panier */}
            {inStock && (
              <div className="flex flex-wrap items-stretch gap-3 mb-8">
                <div className="flex items-center border border-gray-300">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="w-11 h-12 flex items-center justify-center hover:bg-gray-100 transition-colors"
                    aria-label={language === 'fr' ? 'Diminuer la quantité' : 'Decrease quantity'}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-10 text-center font-semibold" data-testid="detail-quantity">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity((q) => Math.min(stockDisponible, q + 1))}
                    className="w-11 h-12 flex items-center justify-center hover:bg-gray-100 transition-colors"
                    aria-label={language === 'fr' ? 'Augmenter la quantité' : 'Increase quantity'}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={isAdding}
                  className={`flex-1 min-w-[180px] h-12 flex items-center justify-center gap-2 px-4 sm:px-8 font-semibold whitespace-nowrap transition-colors ${
                    isAdding
                      ? 'bg-green-600 text-white'
                      : 'bg-primary text-white hover:bg-primary_hover'
                  }`}
                  data-testid="detail-add-to-cart"
                >
                  {isAdding ? (
                    <><Check className="w-5 h-5" /> {t('added')}</>
                  ) : (
                    <><CartPlusIcon className="w-5 h-5" /> {t('addToCart')}</>
                  )}
                </button>
              </div>
            )}

            {/* Rappel livraison — montants issus de lib/delivery.js, pour ne pas
                diverger du panier et de la caisse. */}
            <div className="border border-gray-200 p-5 space-y-2">
              <div className="flex items-center gap-2 font-medium text-text_primary">
                <Truck className="w-5 h-5 text-secondary_hover" />
                {language === 'fr' ? 'Livraison' : 'Delivery'}
              </div>
              <ul className="text-sm text-text_secondary space-y-1">
                <li className="flex justify-between gap-2">
                  <span>Liège</span>
                  <span className="font-medium">{formatPrice(FRAIS_LIEGE, language)}</span>
                </li>
                <li className="flex justify-between gap-2">
                  <span>{language === 'fr' ? 'Périphérie de Liège' : 'Liège outskirts'}</span>
                  <span className="font-medium">{formatPrice(FRAIS_PERIPHERIE, language)}</span>
                </li>
                <li className="flex justify-between gap-2">
                  <span>
                    {language === 'fr' ? 'Reste de la Belgique' : 'Rest of Belgium'}
                    <span className="text-text_secondary/70">
                      {` (min. ${formatPrice(MINIMUM_HORS_LIEGE, language)})`}
                    </span>
                  </span>
                  <span className="font-medium">{formatPrice(FRAIS_HORS_LIEGE, language)}</span>
                </li>
              </ul>
              <p className="text-sm text-text_secondary pt-2 border-t border-gray-200">
                {language === 'fr'
                  ? `Livraison sous ${delaiLivraison('fr')} après confirmation.`
                  : `Delivered within ${delaiLivraison('en')} after confirmation.`}
              </p>
              <p className="text-xs text-text_secondary pt-1">
                {language === 'fr' ? 'Référence : ' : 'Reference: '}
                <span className="font-mono">{product.sku}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
